import React, { use, useEffect } from 'react';
import SubjectMonitoringConfig from './subject-profile/data-listing';
import SubjectProfileDetailsConfig from './subject-profile/subject-profile-details-config';
import ChipList from '@components-clean/core/chip-list';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import SubjectHubConfig from '@pages-clean/admin/study/manage-hubs/subject-monitoring/subject-hub-config';
import { LocalStateContext } from '@contexts/local-state-context';
import { UserContext } from '@contexts/user-context';
import { useTranslation } from '@hooks/index';

const ManageHubsSubjectMonitoring = () => {
    const userContext = use(UserContext);
    const { state } = use(LocalStateContext);

    const { t: tAdmin } = useTranslation('admin', { keyPrefix: 'subjectMonitoring' });
    type SubjectMonitoringType = 'SubjectHub' | 'SubjectProfile';
    const [selected, setSelected] = React.useState<SubjectMonitoringType>('SubjectHub');
    const studyRef = state?.selectedStudy?.id;
    const canEdit = userContext.user!.canWriteSystemAdminLevel!();

    const orgKey = state?.selectedOrganisation?.id;

    const { confirmDiscardChanges, anyComponentHasChanges, discardChanges, componentHasChanges } =
        useUnsavedChangesContext();

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setSelected('SubjectHub');
    }, []);

    return (
        <div className="tab-card">
            <ChipList
                // We use a compound key here to prevent a bug with the focus state of the chiplist causing certain rerenders to scroll the
                // page back to the chiplist.
                key={`${componentHasChanges('subjectMonitoringConfig')} ${componentHasChanges('SUBJECT_PROFILE_DETAILS_CONFIGURATION')}`}
                className="chip"
                data-testid="manage-hubs-subject-monitoring-chip-list"
                defaultData={[
                    {
                        text: tAdmin('chips.subjectHub'),
                        value: 'SubjectHub',
                    },
                    {
                        text: tAdmin('chips.subjectProfile'),
                        value: 'SubjectProfile',
                    },
                ]}
                value={selected}
                onChange={async (e) => {
                    if (!e.value) {
                        return;
                    }
                    if (anyComponentHasChanges()) {
                        const confirmed = await confirmDiscardChanges();
                        if (!confirmed) {
                            return;
                        }
                        discardChanges();
                    }
                    setSelected(e.value as SubjectMonitoringType);
                }}
                selection="single"
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {selected === 'SubjectHub' ? (
                    <SubjectHubConfig orgKey={orgKey} studyRef={studyRef} canEdit={canEdit} />
                ) : (
                    <>
                        <SubjectMonitoringConfig studyRef={studyRef} canEdit={canEdit} />
                        {userContext.user?.organisationId != null && (
                            <SubjectProfileDetailsConfig
                                studyRef={studyRef}
                                organisationId={userContext.user?.organisationId}
                                canEdit={canEdit}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ManageHubsSubjectMonitoring;
